import { BOX_TYPES_MAP, MATERIAL_SLUG_MAP } from '../BoxTypes';

/**
 * Retrieves the box type by its slug.
 * @param {string} typeSlug - The slug representing the box type.
 * @returns {string} - The box type.
 * @throws {Error} - If the slug is invalid.
 */
function getBoxTypeBySlug(typeSlug) {
  const type = BOX_TYPES_MAP[typeSlug];
  if (!type) {
    throw new Error(`Invalid box type: ${typeSlug}`);
  }

  return type
}

/**
 * Validates and converts a slug size to a float.
 * @param {string} size - The slug representing the size.
 * @returns {number} - The size as a float.
 * @throws {Error} - If the size format is invalid.
 */
function serializeSlugSize(size) {
  // Regular expression to match digits (0-9) optionally separated by '_'
  const regex = /^\d+(_\d+)?$/;

  // Split the size by '_' to check both parts
  const sizeParts = size.split('_');

  // Ensure both parts match the regex
  if (sizeParts.some(part => !regex.test(part))) {
    throw new Error(`Invalid size format: ${size}`);
  }

  return parseFloat(size.replace('_', '.'));
}

/**
 * Parses the box size slugs into an object with length, width, and depth.
 * @param {string} lengthSlug - The slug for length.
 * @param {string} widthSlug - The slug for width.
 * @param {string} depthSlug - The slug for depth.
 * @returns {object} - An object containing length, width, and depth.
 */
function parseBoxSizeSlug(lengthSlug, widthSlug, depthSlug) {
  const length = serializeSlugSize(lengthSlug)
  const width = serializeSlugSize(widthSlug)
  const depth = serializeSlugSize(depthSlug)

  return { length, width, depth };
}

/**
 * Retrieves the box material by its slug.
 * @param {string} materialSlug - The slug representing the material.
 * @returns {object} - An object containing outside and inside materials.
 * @throws {Error} - If the material slug is invalid.
 */
function getBoxMaterialBySlug(materialSlug) {
  const material = MATERIAL_SLUG_MAP[materialSlug];
  if (!material) {
    throw new Error(`Invalid material: ${materialSlug}`);
  }
  return material;
}

/**
 * EXPORTED FUNCTION
 * Parses a box slug into an object with detailed box properties.
 *
 * [SKU]-[SIZE]-[MATERIAL(optional)]
 * [SKU]->
 *    mbties (Tuck in extra strong mailing box),
 *    mbti (Tuck in mailing box),
 *    mbtl (tab lock Mailing box),
 *    sb (Shipping box),
 *    pb (Pizza box)
 * [SIZE] -> [length-width-depth] (all in inches)
 * [MATERIAL] ->
 *    kk (outside kraft, inside kraft),
 *    wk (outside white, inside kraft),
 *    ww (outside white, inside white) (pb and sb don’t have this attribute, mbties, mbti, mbtl must have this attribute)
 *
 * Examples:
 *  - mbti-10_25-8_25-4-ww
 *  - mbti-10_25-8_25-4-wk - ovo
 *  - mbtl-10-10-10-wk
 *  - sb-11_25-8_75-6
 *
 * @param {string} boxSlug - The slug representing the box.
 * @returns {object|null} - An object with box properties or null if an error occurs.
 */
function parseBoxSlug(boxSlug) {
  try {
    const parts = boxSlug.split('-');
    const typeKey = parts[0];
    const type = getBoxTypeBySlug(typeKey)
    const { length, width, depth } = parseBoxSizeSlug(parts[1], parts[2], parts[3]);

    const boxVariation = {
      typeCode: typeKey,
      type,
      length,
      width,
      depth
    };

    // Add material details if applicable
    // If no material is specified, default to kraft
    const DEFAULT_MATERIAL = 'wk';
    const materialKey = parts[4] || DEFAULT_MATERIAL
    const material = getBoxMaterialBySlug(materialKey);
    boxVariation.outsideMaterial = material.outside;
    boxVariation.insideMaterial = material.inside;

    return boxVariation;
  } catch (error) {
    console.error(error.message);
    throw new Error('Invalid box slug');
  }
}

/**
 * Retrieves the slug for a given box type.
 * @param {string} type - The box type.
 * @returns {string} - The slug representing the box type.
 */
function getSlugByBoxType(type) {
  return Object.keys(BOX_TYPES_MAP).find(key => BOX_TYPES_MAP[key] === type);
}

/**
 * Retrieves the slug for given outside and inside materials.
 * @param {string} outsideMaterial - The outside material.
 * @param {string} insideMaterial - The inside material.
 * @returns {string} - The slug representing the materials.
 */
function getSlugByBoxMaterial(outsideMaterial, insideMaterial) {
  return Object.keys(MATERIAL_SLUG_MAP).find(key =>
    MATERIAL_SLUG_MAP[key].outside === outsideMaterial && MATERIAL_SLUG_MAP[key].inside === insideMaterial);
}

/**
 * EXPORTED FUNCTION
 * Serializes a box object into a slug string.
 * @param {object} box - The box object.
 * @returns {string} - The serialized slug string.
 */
function serializeBox(box) {
  const typeKey = getSlugByBoxType(box.type);
  const length = box.length.toString().replace('.', '_');
  const width = box.width.toString().replace('.', '_');
  const depth = box.depth.toString().replace('.', '_');

  let serializedString = `${typeKey}-${length}-${width}-${depth}`;

  if (typeKey.startsWith('mb')) {
    const materialKey = getSlugByBoxMaterial(box.outsideMaterial, box.insideMaterial);
    serializedString += `-${materialKey}`;
  }

  return serializedString;
}

export default parseBoxSlug

export {
  parseBoxSlug,
  serializeBox
}
